import { analysisServiceForMenu } from '@/composables/saju-insight/useAnalysisSajuContext';
import store from '@/store';
import { useLoginStore } from '@/store/modules/login';
import { useMyInfoStore } from '@/store/modules/myinfo';
import { canNavigate } from '@layouts/plugins/casl';

// title 관리는 @unhead/vue가 App.vue에서 담당. (이전 router.afterEach 삭제)

export const setupGuards = (router) => {
  router.beforeEach(async (to, from) => {
    const loginStore = useLoginStore();

    // smartwel 회원은 낭만P 충전(/purchase/points) 진입 시 복지포인트 전환 페이지로 redirect.
    // 단계4에서 명시적으로 낭만충전을 선택한 경우(from=exchange)는 우회 통과.
    if (to.path === '/purchase/points' && to.query.from !== 'exchange') {
      try {
        const myInfoStore = useMyInfoStore();
        if (myInfoStore.isSmartwelMember) {
          return { path: '/purchase/exchange', query: to.query, replace: true };
        }
      } catch {}
    }

    // 비-smartwel 회원의 /purchase/exchange 직접 진입 차단 → 일반 충전 페이지로 redirect
    if (to.path === '/purchase/exchange') {
      try {
        const myInfoStore = useMyInfoStore();
        if (!myInfoStore.isSmartwelMember) {
          return { path: '/purchase/points', query: to.query, replace: true };
        }
      } catch {}
    }

    if (to.meta.public) return true;

    // 앱 1.3.2+: 로그인 페이지 접근 시 네이티브 로그인으로 위임
    if (window.__APP_VERSION__ && to.path === '/auth/login') {
      window.ReactNativeWebView?.postMessage(JSON.stringify({
        action: 'login',
      }));
      return false;
    }

    // 모바일 웹 온보딩 리다이렉트 (RN 앱, 검색 크롤러 제외)
    const isApp = !!window.ReactNativeWebView || !!window.__APP_VERSION__;
    const isCrawler =
      /Googlebot|bingbot|Yeti|Daumoa|facebookexternalhit|Twitterbot|Slackbot|KakaoTalk-Scrap/i.test(
        navigator.userAgent,
      );
    const isMobileWeb =
      !isApp && !isCrawler && /Mobi|Android|iPhone/i.test(navigator.userAgent);
    if (
      isMobileWeb &&
      !localStorage.getItem('nangman_use_mobile_web') &&
      to.path !== '/onboarding' &&
      !to.path.startsWith('/nangman-center')
    ) {
      try {
        sessionStorage.setItem('nangman_onboarding_redirect', to.fullPath);
      } catch {}
      return { path: '/onboarding' };
    }

    const isLoggedIn = !!(store.getters['user/data'] && store.getters['user/token']);

    // 이전 모바일 링크는 사주 선택을 거쳐 통합 상세분석으로 진입한다.
    const mobileService = to.path === '/m/saju-insight/nangman-harmony' ? 'Harmony'
      : to.path === '/m/saju-insight/nangman-naming' ? 'Naming' : null;
    if (mobileService && !to.query.id) {
      return { path: '/saju-service', query: { menu: `/overviewNangman${mobileService}` } };
    }

    // 앱 1.3.2+: 상세분석 → 모바일 분석 페이지로 리다이렉트
    if (window.__APP_VERSION__ && to.path.startsWith('/analysis')) {
      const id = to.params.id || to.path.split('/analysis/')[1];
      const service = analysisServiceForMenu(to.query.menu);
      if (id && service) return {
        path: `/m/saju-insight/nangman-${service}`,
        query: { ...to.query, id },
      };
      return id ? { path: `/m/saju-insight/${id}`, query: to.query } : { path: '/saju-service' };
    }

    // 상세분석 페이지 화면 크기 기반 리다이렉트
    const isMobileWidth = window.innerWidth <= 960;
    if (to.path.startsWith('/analysis') && isMobileWidth) {
      const id = to.params.id || to.path.split('/analysis/')[1];
      const service = analysisServiceForMenu(to.query.menu);
      if (id && service) return {
        path: `/m/saju-insight/nangman-${service}`,
        query: { ...to.query, id },
      };
      return id ? { path: `/m/saju-insight/${id}`, query: to.query } : { path: '/saju-service' };
    }

    // 비로그인 접근 차단 (낭만분석, 상세분석, 마이페이지, 낭만궁합·낭만작명 PC/모바일)
    const protectedPaths = ['/saju-service', '/mypage', '/analysis', '/saju-insight', '/saju-naming', '/m/saju-insight/nangman-harmony', '/m/saju-insight/nangman-naming'];
    const isProtected = protectedPaths.some((p) => to.path === p || to.path.startsWith(p + '/'));
    if (isProtected && !isLoggedIn) {
      // 앱: sessionStorage에 토큰이 있으면 Vuex 미반영 상태라도 통과
      if (isApp && sessionStorage.getItem('X-Token')) return true;
      // 직접 URL 접근 또는 로그인 화면에서 뒤로가기 → 패스
      if (!from.name || from.path === '/auth/login') return true;
      // 로그인 모달 표시 (App.vue에서 처리)
      const isServiceEntry = to.path === '/saju-insight' || to.path === '/saju-naming' ||
        ((to.path === '/saju-service' || to.path.startsWith('/analysis/')) && analysisServiceForMenu(to.query.menu));
      const confirmLogin = await loginStore.showLoginModal(isServiceEntry ? to.fullPath : null);
      return confirmLogin;
    }

    if (to.meta.unauthenticatedOnly) {
      if (isLoggedIn) return '/';
      else return undefined;
    }

    const canAccess = await canNavigate(to);

    if (!canAccess) {
      // 로그인된 사용자가 권한이 없을 경우 Not Authorized 페이지로 이동
      return {
        name: 'login',
        query: {
          ...to.query,
          to: to.fullPath !== '/' ? to.fullPath : undefined,
        },
      };
    }

    return true;
  });
};
